"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import CSPDebugger from "@/components/layout/CSPDebugger";
import Navbar from "@/components/navbar/Navbar";
import { Toaster } from "@/components/ui/sonner";
import { NotificationProvider } from "@/context/NotificationContext";
import { ToastProvider } from "@/context/ToastContext";
import { AuthProvider, useAuth } from "@/context/providers/AuthProvider";
import { ConditionalChatProvider } from "@/context/providers/ConditionalChatProvider";
import { CreditsProvider } from "@/context/providers/CreditsProvider";
import QueryProvider from "@/context/providers/QueryProvider";
import ErrorBoundary from "@/utils/errorBoundary";
import type { ServerAuthSession } from "@/types/auth";
import { DeferredPublicGlobalClientUtilities } from "./DeferredPublicGlobalClientUtilities";
import { GlobalClientUtilities } from "./GlobalClientUtilities";

interface RouteAwareShellProps {
  children: React.ReactNode;
  gaId: string;
  initialSession?: ServerAuthSession | null;
}

const FULL_APP_ROUTE_PREFIXES = [
  "/account",
  "/admin",
  "/checkout",
  "/create",
  "/credits",
  "/login",
  "/messages",
  "/requests",
  "/reset-password",
  "/seller-onboarding",
  "/success",
  "/ticket",
  "/track",
  "/test-password-reset",
];

function matchesRoutePrefix(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function needsFullAppUtilities(pathname: string) {
  return FULL_APP_ROUTE_PREFIXES.some(prefix =>
    matchesRoutePrefix(pathname, prefix)
  );
}

function AuthReturnMarkerCleanup() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || !user || typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get("_ssr_oauth") !== "1") {
      return;
    }

    try {
      window.sessionStorage?.removeItem("auth_redirect_url");
    } catch {
      // Storage can be unavailable; server-auth state has still hydrated.
    }

    url.searchParams.delete("_ssr_oauth");
    window.history.replaceState(
      window.history.state,
      "",
      url.pathname + url.search + url.hash
    );
  }, [loading, user]);

  return null;
}

export function RouteAwareShell({
  children,
  gaId,
  initialSession,
}: RouteAwareShellProps) {
  const pathname = usePathname() || "/";
  const useFullUtilities = needsFullAppUtilities(pathname);
  const isCreateWorkspace = matchesRoutePrefix(pathname, "/create");

  return (
    <>
      <ErrorBoundary>
        {/* Theme is provided once at the root (RootLayoutShell). */}
        <QueryProvider>
          <AuthProvider
            initialSession={useFullUtilities ? initialSession : null}
            roleHydrationMode={useFullUtilities ? "eager" : "idle"}
            verifyNullServerSnapshot={!useFullUtilities}
          >
            <CreditsProvider>
              <ToastProvider>
                {useFullUtilities && (
                  <>
                    <Toaster position="top-right" richColors />
                    <CSPDebugger />
                  </>
                )}
                <NotificationProvider
                  enablePolling={false}
                  autoMarkAsRead={false}
                  loadOnMount={true}
                  refreshOnFocus={true}
                >
                  <AuthReturnMarkerCleanup />
                  {!isCreateWorkspace ? (
                    <Navbar initialCredits={undefined} />
                  ) : null}
                  <ConditionalChatProvider>
                    <main
                      id="main-content"
                      tabIndex={-1}
                      className="app-main-surface flex flex-col flex-1 min-h-0 min-w-0 focus:outline-none"
                    >
                      <ErrorBoundary>{children}</ErrorBoundary>
                    </main>
                  </ConditionalChatProvider>
                </NotificationProvider>
              </ToastProvider>
            </CreditsProvider>
          </AuthProvider>
        </QueryProvider>
      </ErrorBoundary>
      {useFullUtilities ? (
        <GlobalClientUtilities gaId={gaId} />
      ) : (
        <DeferredPublicGlobalClientUtilities gaId={gaId} />
      )}
    </>
  );
}
